import { getDashboardLayout, saveDashboardLayout } from '../services/dashboardLayoutService';
import {
    buildDefaultLayout,
    sanitizeLayout,
    getWidgetDefinition,
    buildWidgetSiteContext
} from '../widgets/widgetRegistry';

function cloneLayout(layout) {
    return JSON.parse(JSON.stringify(layout));
}

export default {
    state: {
        /** Working layout rendered by the grid (mutated while editing). */
        layout: null,
        /** Last layout persisted to (or loaded from) the backend; used by Cancel. */
        savedLayout: null,
        isEditMode: false,
        isDirty: false,
        isLayoutLoaded: false,
        isSavingLayout: false
    },
    getters: {
        dashboardLayout(state) {
            return state.layout;
        },
        isDashboardEditMode(state) {
            return state.isEditMode;
        },
        isDashboardLayoutDirty(state) {
            return state.isDirty;
        },
        isDashboardLayoutLoaded(state) {
            return state.isLayoutLoaded;
        },
        isSavingDashboardLayout(state) {
            return state.isSavingLayout;
        }
    },
    mutations: {
        setDashboardLayout(state, layout) {
            state.layout = layout;
        },
        setSavedDashboardLayout(state, layout) {
            state.savedLayout = layout;
        },
        setDashboardLayoutLoaded(state, value) {
            state.isLayoutLoaded = !!value;
        },
        setDashboardEditMode(state, value) {
            state.isEditMode = !!value;
        },
        setDashboardLayoutDirty(state, value) {
            state.isDirty = !!value;
        },
        setSavingDashboardLayout(state, value) {
            state.isSavingLayout = !!value;
        },
        updateDashboardWidgetConfig(state, { widgetId, config }) {
            const item = (state.layout || []).find((layoutItem) => layoutItem.i === widgetId);
            if (!item) return;
            item.config = config;
            state.isDirty = true;
        },
        removeDashboardWidget(state, { widgetId }) {
            if (!state.layout) return;
            state.layout = state.layout.filter((layoutItem) => layoutItem.i !== widgetId);
            state.isDirty = true;
        },
        addDashboardWidget(state, { widgetType, widgetId }) {
            const definition = getWidgetDefinition(widgetType);
            if (!definition || !state.layout || !widgetId) return;
            const maxY = state.layout.reduce((max, item) => Math.max(max, item.y + item.h), 0);
            state.layout.push({
                i: widgetId,
                x: 0,
                y: maxY,
                w: definition.defaultW,
                h: definition.defaultH,
                widgetType,
                config: { ...definition.defaultConfig }
            });
            state.isDirty = true;
        }
    },
    actions: {
        /**
         * Load the user's saved layout; falls back to the default layout
         * (mirroring the classic Overview) when nothing is saved or the request fails.
         */
        async loadDashboardLayout(context, payload = {}) {
            const { dashboardKey = 'overview', force = false } = payload;
            if (context.state.isLayoutLoaded && !force) return context.state.layout;
            let layout = null;
            try {
                const savedLayout = await getDashboardLayout(dashboardKey);
                layout = sanitizeLayout(savedLayout);
            } catch (error) {
                console.error('[dashboardLayoutStore] Failed to load layout, using default', error);
                layout = null;
            }
            if (!layout) layout = buildDefaultLayout(buildWidgetSiteContext(context.rootGetters));
            context.commit('setDashboardLayout', cloneLayout(layout));
            context.commit('setSavedDashboardLayout', cloneLayout(layout));
            context.commit('setDashboardLayoutLoaded', true);
            context.commit('setDashboardLayoutDirty', false);
            return context.state.layout;
        },
        async saveDashboardLayout(context, payload = {}) {
            const { dashboardKey = 'overview' } = payload;
            const layoutToSave = sanitizeLayout(context.state.layout);
            if (!layoutToSave) throw new Error('Cannot save an empty dashboard layout');
            context.commit('setSavingDashboardLayout', true);
            try {
                const savedLayout = await saveDashboardLayout(dashboardKey, layoutToSave);
                const normalized = sanitizeLayout(savedLayout) || layoutToSave;
                context.commit('setDashboardLayout', cloneLayout(normalized));
                context.commit('setSavedDashboardLayout', cloneLayout(normalized));
                context.commit('setDashboardLayoutDirty', false);
                context.commit('setDashboardEditMode', false);
                return normalized;
            } finally {
                context.commit('setSavingDashboardLayout', false);
            }
        },
        resetDashboardLayoutToDefault(context) {
            const layout = buildDefaultLayout(buildWidgetSiteContext(context.rootGetters));
            context.commit('setDashboardLayout', cloneLayout(layout));
            context.commit('setDashboardLayoutDirty', true);
        },
        addDashboardWidget(context, { widgetType }) {
            const widgetId = `${widgetType}-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
            context.commit('addDashboardWidget', { widgetType, widgetId });
            return widgetId;
        },
        /** Discard unsaved edits and leave edit mode. */
        cancelDashboardLayoutEdit(context) {
            const savedLayout =
                context.state.savedLayout || buildDefaultLayout(buildWidgetSiteContext(context.rootGetters));
            context.commit('setDashboardLayout', cloneLayout(savedLayout));
            context.commit('setDashboardLayoutDirty', false);
            context.commit('setDashboardEditMode', false);
        }
    }
};
